import React, { useEffect, useState, useRef } from 'react';

import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import {
  Banner,
  Button,
  ButtonText,
  Switch,
  TextArea,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/pro-regular-svg-icons';
import {
  IFeedbackForAuthors,
  IReviewerAttachment
} from '@publishing-engine/domain/models';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useModeratePeerReview } from 'src/api/commands';
import { IPeerReviewListItem } from '@publishing-engine/domain/views/review';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import ProtectedReviewWorkflowAction from 'src/components/ProtectedAction/ProtectedReviewWorkflowAction';
import { IModerateReviewSubTypes } from '@publishing-engine/domain/actions/v1/peerReviewWorkflow/peerReviewModerate';
import FileViewerButtonText from '../FileViewerButtonText';

type AuthorReviewForm = {
  feedbackForAuthors: IFeedbackForAuthors;
  reviewerAttachments?: IReviewerAttachment[];
};
interface Props {
  review: IPeerReviewListItem;
  reviewFieldConfig: any;
  submissionId: string;
}

const AuthorReview: React.FC<Props> = ({ review, submissionId }) => {
  const { availableReviewActions, organizationId, isRevisionRound } =
    useSubmissionData();
  const isModeratingLocked = isRevisionRound
    ? !availableReviewActions.contains(
        review,
        'PeerReviewModerate#revisionAssessment'
      )
    : !availableReviewActions.contains(
        review,
        'PeerReviewModerate#firstAssessment'
      );
  const moderateReviewSubType: IModerateReviewSubTypes = isRevisionRound
    ? 'revisionAssessment'
    : 'firstAssessment';

  const submissionContextId = useCurrentContextId() ?? '';

  const reviewAssessment =
    review.caseData.revisionAssessment ?? review.caseData.firstAssessment;

  const feedbackForAuthors = reviewAssessment?.feedbackForAuthors;
  const reviewerAttachments = reviewAssessment?.reviewerAttachments;
  // state
  const [isModerating, setIsModerating] = useState<boolean>(false);

  // hooks
  const toast = useToast();
  const isInitialRenderVisibility = useRef(true);
  const isInitialRenderAttachments = useRef(true);
  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isDirty },
    watch,
    setValue,
    getValues
  } = useForm<AuthorReviewForm>({
    defaultValues: {
      feedbackForAuthors,
      reviewerAttachments
    },
    mode: 'onChange'
  });

  const moderateFirstAssessment = useModeratePeerReview({
    submissionId: review.submission.id,
    reviewId: review.id,
    organizationId,
    submissionContextId,
    reviewerId: review.reviewer.id
  });

  const { fields, update } = useFieldArray({
    control,
    name: 'reviewerAttachments'
  });

  const watchReviewAttachments = watch('reviewerAttachments');
  const watchModerated = watch('feedbackForAuthors.moderated');
  const watchFeedbackForAuthorsVisibility = watch(
    'feedbackForAuthors.visibility'
  );

  const updateFirstAssessment = async (
    data: AuthorReviewForm,
    toastFor?: 'visibility' | 'reviewAttachments' | 'comment'
  ) => {
    const response = await moderateFirstAssessment.mutateAsync({
      firstAssessment: data,
      subtype: moderateReviewSubType
    });

    if (!toastFor) return;

    if (response.isSuccess) {
      let title = '';
      let description = '';

      if (toastFor === 'visibility') {
        title = 'Feedback Visibility Updated';
      } else if (toastFor === 'reviewAttachments') {
        title = 'Review Attachments Access Updated';
      } else if (toastFor === 'comment') {
        title = 'Moderated Feedback for Authors Saved';
        description =
          'Your feedback was saved, with the original feedback unchanged. Check the “For Authors” section for more details.';
      }

      toast({
        variant: 'success',
        title,
        description,
        isClosable: true
      });
    } else {
      toast({
        variant: 'error',
        title: 'Failed to update feedback',
        isClosable: true
      });
    }

    return response;
  };

  useEffect(() => {
    if (isInitialRenderVisibility.current || !isDirty) {
      isInitialRenderVisibility.current = false;
      return;
    }
    updateFirstAssessment(
      {
        feedbackForAuthors: {
          ...getValues('feedbackForAuthors'),
          visibility: watchFeedbackForAuthorsVisibility
        }
      },
      'visibility'
    );
  }, [watchFeedbackForAuthorsVisibility]);

  useEffect(() => {
    if (isInitialRenderAttachments.current || !isDirty) {
      isInitialRenderAttachments.current = false;
      return;
    }
    updateFirstAssessment(
      {
        feedbackForAuthors: getValues('feedbackForAuthors'),
        reviewerAttachments: watchReviewAttachments
      },
      'reviewAttachments'
    );
  }, [watchReviewAttachments]);

  const onSubmit = async (data: AuthorReviewForm) => {
    await updateFirstAssessment(data, 'comment');
    setIsModerating(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex
        gap={8}
        background="whiteSecondaryFill"
        flexDirection="column"
        marginY={4}
      >
        {feedbackForAuthors?.moderated && (
          <Banner
            variant="attention"
            heading="For your attention"
            content="In case of rejection or revision, Authors will only see the Moderated Feedback, which can still be adjusted as needed."
          />
        )}

        {feedbackForAuthors?.original && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">Feedback for Authors:</Text>
            {isModerating && !feedbackForAuthors?.moderated ? (
              <Controller
                name="feedbackForAuthors.moderated"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    isInvalid={!!errors.feedbackForAuthors?.moderated}
                    value={watchModerated || feedbackForAuthors?.original || ''}
                  />
                )}
              />
            ) : (
              <Text textStyle="textBody">{feedbackForAuthors?.original}</Text>
            )}

            {isModerating && !feedbackForAuthors?.moderated ? (
              <Flex gap={2} justifyContent="flex-start">
                <Button
                  variant="secondary"
                  onClick={() => setIsModerating(false)}
                  borderRadius="small"
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" borderRadius="small">
                  Save Changes
                </Button>
              </Flex>
            ) : (
              <>
                {!feedbackForAuthors?.moderated && (
                  <Box>
                    <ProtectedReviewWorkflowAction
                      actionName={`PeerReviewModerate#${moderateReviewSubType}`}
                      reviewId={review.id}
                    >
                      <ButtonText
                        onClick={() => setIsModerating(true)}
                        isDisabled={isModeratingLocked}
                      >
                        Moderate Feedback
                      </ButtonText>
                    </ProtectedReviewWorkflowAction>
                  </Box>
                )}
              </>
            )}
          </Flex>
        )}

        {feedbackForAuthors?.moderated && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">Moderated Feedback for Authors:</Text>
            {isModerating ? (
              <Controller
                name="feedbackForAuthors.moderated"
                control={control}
                render={({ field }) => (
                  <TextArea
                    {...field}
                    isInvalid={!!errors.feedbackForAuthors?.moderated}
                    value={watchModerated || feedbackForAuthors.moderated || ''}
                  />
                )}
              />
            ) : (
              <Text textStyle="textBody">{feedbackForAuthors.moderated}</Text>
            )}

            {isModerating ? (
              <Flex gap={2} justifyContent="flex-start">
                <Button
                  variant="secondary"
                  onClick={() => setIsModerating(false)}
                  borderRadius="small"
                >
                  Cancel
                </Button>
                <Button variant="primary" type="submit" borderRadius="small">
                  Save Changes
                </Button>
              </Flex>
            ) : (
              <Box>
                <ProtectedReviewWorkflowAction
                  actionName={`PeerReviewModerate#${moderateReviewSubType}`}
                  reviewId={review.id}
                >
                  <ButtonText
                    onClick={() => setIsModerating(true)}
                    isDisabled={isModeratingLocked}
                  >
                    Moderate Feedback
                  </ButtonText>
                </ProtectedReviewWorkflowAction>
              </Box>
            )}
          </Flex>
        )}

        {typeof reviewAssessment?.missingReference?.exists === 'boolean' && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingLine">Relevance of Cited Work</Text>
            <Text textStyle="textBody">
              {reviewAssessment?.missingReference?.exists
                ? 'No: The author did not reference recent and appropriate work.'
                : 'Yes: The author referenced recent and appropriate work.'}
            </Text>
          </Flex>
        )}

        {reviewAssessment?.missingReference?.exists && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingLine">Recommended References</Text>
            <Text textStyle="textBody">
              {reviewAssessment.missingReference.value}
            </Text>
          </Flex>
        )}

        {feedbackForAuthors?.visibility && (
          <Flex justifyContent="space-between" alignItems="center">
            <Box>
              <Text textStyle="headingItem">
                Exclude Feedback from Response to Author
              </Text>
              <Text textStyle="textBody">
                If Invited to Revise, Authors will not have access to this
                feedback.
              </Text>
            </Box>

            <Controller
              {...register('feedbackForAuthors.visibility')}
              control={control}
              render={({ field }) => (
                <Switch
                  {...field}
                  isChecked={watchFeedbackForAuthorsVisibility === 'show'}
                  isDisabled={isModeratingLocked}
                  onChange={event => {
                    setValue(
                      'feedbackForAuthors.visibility',
                      event.target.checked ? 'show' : 'hide'
                    );
                  }}
                />
              )}
            />
          </Flex>
        )}

        {(reviewerAttachments || []).length > 0 ? (
          <Flex flexDirection="column" gap={4}>
            <Text textStyle="headingItem">Review Attachments</Text>
            <Banner
              variant="attention"
              heading="For your attention"
              content="Decide on the files that the Author should have access in case of rejection or revision."
            />

            <Flex flexDirection="column" gap={4} marginTop={4}>
              {fields?.map((attachment, index) => (
                <React.Fragment key={attachment.id}>
                  <Flex justifyContent="space-between">
                    <Flex gap={2} alignItems="center" flex={1} minWidth={0}>
                      <FontAwesomeIcon icon={faFile} />
                      <Text textStyle="textBody" wordBreak="break-word" whiteSpace="normal" flex={1} minWidth={0}>{attachment.name}</Text>
                      <FileViewerButtonText
                        file={attachment}
                        submissionContextId={submissionId}
                        textStyle="textBody"
                        flexShrink={0}
                      >
                        View
                      </FileViewerButtonText>
                    </Flex>

                    <Controller
                      name={`reviewerAttachments.${index}.id`}
                      control={control}
                      render={({ field }) => (
                        <ProtectedReviewWorkflowAction
                          actionName={`PeerReviewModerate#${moderateReviewSubType}`}
                          reviewId={review.id}
                        >
                          <Switch
                            {...field}
                            isChecked={attachment?.visibility === 'show'}
                            isDisabled={isModeratingLocked}
                            onChange={event => {
                              update(index, {
                                ...attachment,
                                visibility: event.target.checked
                                  ? 'show'
                                  : 'hide'
                              });
                            }}
                          />
                        </ProtectedReviewWorkflowAction>
                      )}
                    />
                  </Flex>
                  {index < (reviewerAttachments?.length || 0) - 1 ? (
                    <Divider />
                  ) : null}
                </React.Fragment>
              ))}
            </Flex>
          </Flex>
        ) : null}
      </Flex>
    </form>
  );
};

export default AuthorReview;
